import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import clsx from 'clsx';

// MUI components
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
} from '@material-ui/core';

// MUI Icons
import RemoveRedEyeIcon from '@material-ui/icons/RemoveRedEye';

// components
import useStyles from './styles';
import { UNAUTHORIZED } from '../../utils/const/http';
import { parseDate } from '../../utils/date';

import Snackbar from '../../components/Snackbar';
import Pagination from '../../components/Pagination';

// API calls
import { getTaskResponses } from '../../requests/api/task-response';

// eslint-disable-next-line no-shadow
const QA = () => {
  const styles = useStyles();
  const { t } = useTranslation();

  const states = [
    { value: '', label: 'Todos' },
    { value: 'completed', label: t('qa.status.completed') },
    { value: 'pending', label: t('qa.status.pending') },
    { value: 'approved', label: t('qa.status.approved') },
    { value: 'rejected', label: t('qa.status.rejected') },
    { value: 'objected', label: t('qa.status.objected') },
    { value: 'sincronized', label: t('qa.status.sincronized') },
  ];
  // table states
  const [page, setPage] = useState(0);
  const [rowsToShow, setRowsToShow] = useState(null);
  const rowsPerPage = 10;
  const [taskResponsesQuantity, setTaskResponsesQuantity] = useState(0);

  // auxiliar states

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [succ, setSucc] = useState('');

  const [filters, setFilters] = useState({
    id: '',
    surveyTitle: '',
    location: '',
    email: '',
    dateStart: '',
    dateEnd: '',
    state: '',
  });

  const company = useSelector((state) => state.company);

  const tableHeaders = [
    {
      id: 'surveyResponseId',
      label: <b>{t('qa.tableHeaders.surveyResponseId')}</b>,
    },
    {
      id: 'locationName',
      label: <b>{t('qa.tableHeaders.locationName')}</b>,
    },
    {
      id: 'taskName',
      label: <b>{t('qa.tableHeaders.taskName')}</b>,
    },
    {
      id: 'category',
      label: <b>{t('qa.tableHeaders.category')}</b>,
    },
    {
      id: 'userName',
      label: <b>{t('qa.tableHeaders.userName')}</b>,
    },
    {
      id: 'reviewedBy',
      label: <b>{t('qa.tableHeaders.reviewedBy')} (ID)</b>,
    },
    {
      id: 'startedAt',
      label: <b>{t('qa.tableHeaders.startedAt')}</b>,
    },
    {
      id: 'finishedAt',
      label: <b>{t('qa.tableHeaders.finishedAt')}</b>,
    },
    {
      id: 'status',
      label: <b>{t('qa.tableHeaders.status')}</b>,
    },
    {
      id: 'actions',
      label: <b>{t('qa.tableHeaders.actions')}</b>,
    },
  ];

  const requestSearch = useCallback(async (pageOfTable, limit) => {
    const response = await getTaskResponses(
      pageOfTable,
      limit,
      company.companyId,
      filters.id,
      filters.surveyTitle,
      filters.location,
      filters.email,
      filters.dateStart,
      filters.dateEnd,
      filters.state,
    );
    if (response.success) {
      setRowsToShow(response.data.data.taskResponses);
      setTaskResponsesQuantity(response.data.data.totalTaskResponses);
      setLoading(false);
    }

    if (response.status === UNAUTHORIZED.status) {
      window.location.href = '/home';
    }
  }, [company.companyId, filters]);

  const handleChangePage = (newPage) => {
    const numberNewPage = Number.parseInt(newPage, 10);
    setPage(numberNewPage);
  };

  const load = () => {
    setLoading(true);
  };

  useEffect(() => {
    load();
  }, [rowsToShow]);

  useEffect(() => {
    requestSearch(page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleState = (taskResponseReview) => {
    /* eslint-disable no-param-reassign */
    if (taskResponseReview == null) taskResponseReview = { status: 'pending' };

    switch (taskResponseReview.status) {
      case 'completed':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagOpen}`}>O <b>{t('qa.status.completed')}</b></p>
        );
      case 'pending':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagWaitinfForApproval}`}>O <b>{t('qa.status.pending')}</b></p>
        );
      case 'approved':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagApproved}`}>O <b>{t('qa.status.approved')}</b></p>
        );
      case 'rejected':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagRejected}`}>O <b>{t('qa.status.rejected')}</b></p>
        );
      case 'objected':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagObjected}`}>O <b>{t('qa.status.objected')}</b></p>
        );
      case 'sincronized':
        return (
          <p align="left" className={`${styles.tag} ${styles.tagClose}`}>O <b>{t('qa.status.sincronized')}</b></p>
        );
      default:
        return (
          <p align="left" className={`${styles.tag} ${styles.tagNA}`}>O <b>N/A</b></p>
        );
    }
  };

  const handleClear = () => {
    setFilters({
      id: '',
      surveyTitle: '',
      location: '',
      email: '',
      dateStart: '',
      dateEnd: '',
      state: '',
    });
  };

  const handleChange = (key, value) => {
    setFilters({
      ...filters,
      [key]: value,
    });
  };

  const applyFilters = () => {
    setPage(0);
    requestSearch(0, rowsPerPage);
  };

  const getSurveyResponseReview = (surveyResponseReviews) => {
    if (surveyResponseReviews && surveyResponseReviews.length > 0) {
      return surveyResponseReviews[0];
    }
    return null;
  };

  const content = () => {
    if (!taskResponsesQuantity) {
      return (
        <TableRow>
          <TableCell colSpan={9} className={styles.users}>
            <h4>{t('qa.noTaskResponses')}</h4>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {rowsToShow
          .map((row) => (
            <TableRow
              hover
              key={row.id}
            >{row.TaskInstance.Survey
              && <>
                <TableCell align="left">{row.id ? row.id : '-'}</TableCell>
                <TableCell
                  component="th"
                  scope="row"
                  padding="none"
                  className={styles.locationName}
                  width="20%"
                >{row.TaskInstance.Location ? row.TaskInstance.Location.name : t('qa.noLocationName')}</TableCell>
                <TableCell align="left">{row.TaskInstance.Survey ? row.TaskInstance.Survey.title : t('qa.noTaskName')}</TableCell>
                <TableCell align="left">{row.TaskInstance.Category ? `${row.TaskInstance.Category.name} (${row.TaskInstance.Category.code})` : 'General'}</TableCell>
                <TableCell align="left">{row.TaskInstance.User ? row.TaskInstance.User.fullName : t('qa.noUserName')}</TableCell>
                <TableCell align="center">{getSurveyResponseReview(row.SurveyResponseReviews) ? getSurveyResponseReview(row.SurveyResponseReviews).reviewedBy : '---'}</TableCell>
                <TableCell align="center">{parseDate(row.startedAt)}</TableCell>
                <TableCell align="center">{parseDate(row.finishedAt)}</TableCell>
                <TableCell align="center">{handleState(getSurveyResponseReview(row.SurveyResponseReviews))}</TableCell>
                <TableCell align="center">
                  <IconButton component={Link} to={`qa/${row.id}`}>
                    <RemoveRedEyeIcon title={t('qa.review')} />
                  </IconButton>
                </TableCell>
              </>
              }
            </TableRow>
          ))}
      </>
    );
  };

  const searchBar = () => (
    <div className={styles.filtersContainer}>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 3,
          alignItems: 'center',
        }}
      >
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.id')}
          variant="outlined"
          size="small"
          value={filters.id}
          onChange={(e) => handleChange('id', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.surveyTitle')}
          variant="outlined"
          size="small"
          value={filters.surveyTitle}
          onChange={(e) => handleChange('surveyTitle', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.location')}
          variant="outlined"
          size="small"
          value={filters.location}
          onChange={(e) => handleChange('location', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.email')}
          variant="outlined"
          size="small"
          value={filters.email}
          onChange={(e) => handleChange('email', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.dateStart')}
          type="date"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          value={filters.dateStart}
          onChange={(e) => handleChange('dateStart', e.target.value)}
          fullWidth
        />
        <TextField
          className={styles.filterText}
          label={t('qa.searchBar.dateEnd')}
          type="date"
          InputLabelProps={{ shrink: true }}
          variant="outlined"
          size="small"
          value={filters.dateEnd}
          onChange={(e) => handleChange('dateEnd', e.target.value)}
          fullWidth
        />
        <FormControl
          className={styles.filterText}
          variant="outlined"
          size="small"
          fullWidth
        >
          <InputLabel className={styles.inputLabel} shrink>{t('qa.searchBar.state')}</InputLabel>
          <Select
            label={t('qa.searchBar.state')}
            value={filters.state}
            onChange={(e) => handleChange('state', e.target.value)}
            displayEmpty
          >
            {states.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          marginTop: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="medium"
          className={styles.applyFilterButton}
          onClick={() => applyFilters()}
        >
          Aplicar filtros
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          className={styles.cleanFiltersButton}
          onClick={handleClear}
        >
          Limpiar filtros
        </Button>
      </Box>
    </div>
  );

  return (
    <div className={styles.content}>
      <div className={styles.header}>

        <Box sx={{ width: '100%' }}>
          <Typography variant='h1' className={styles.title} gutterBottom>
            {t('qa.title')}
          </Typography>
          <Typography variant='h2' className={styles.subtitle} gutterBottom display="inline">
            {t('qa.subtitle')}
          </Typography>
        </Box>
      </div>
      {searchBar()}
      <Paper className={styles.taskResponseTable}>
        <>
          <TableContainer>
            <Table
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((tableHeader) => (
                    <TableCell
                      key={tableHeader.id}
                      className={clsx({
                        [styles.tableHeader]: true,
                        [styles.taskNameHeader]: ['locationName', 'taskName', 'userName', 'category', 'surveyResponseId'].includes(tableHeader.id),
                      })}
                      padding="normal"
                      align="left"
                    >
                      {tableHeader.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className={styles.progress}>
                      <CircularProgress />
                      <h4>{t('qa.loading')}</h4>
                    </TableCell>
                  </TableRow>
                ) : content()}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            setPage={setPage}
            handleChangePage={handleChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
            quantity={taskResponsesQuantity}
          />
        </>
      </Paper>
      <div>
        <Snackbar
          open={err !== ''}
          message={err}
          severity="error"
          onClose={() => setErr('')}
        />
        <Snackbar
          open={succ !== ''}
          message={succ}
          severity="success"
          onClose={() => setSucc('')}
        />
      </div>
    </div>
  );
};

export default QA;
